'use client';

/**
 * FormField (storefront, live) — the real input, registered with
 * react-hook-form via FormContext. Validation (required / min / max /
 * type pattern) matches the server-side rules exactly. Falls back to the
 * shared read-only preview when placed outside a FormContainer.
 */

import React from 'react';
import { FormField as Shared, inputSurface } from '@launchstore/shared-puck';
import type { FormFieldProps } from '@launchstore/shared-puck';
import { resolveColor } from '@launchstore/shared-puck';
import { useFormContext } from './FormContext';
import { buildValidationRules, inputTypeOf, fieldIds } from './form-live-helpers';

function FormFieldLive(props: FormFieldProps & { id?: string }) {
  const ctx = useFormContext();
  if (!ctx) return <>{(Shared.render as any)(props as FormFieldProps)}</>;

  const { register, errors } = ctx;
  const ids = fieldIds(props.id);
  const error = errors[props.label];
  const rules = buildValidationRules(props);
  const surface = inputSurface(props);

  return (
    <div className={`mt-${props.marginTop || '0'} ${props.fieldWidth === 'half' ? 'w-1/2' : 'w-full'}`}>
      <label
        htmlFor={ids.input}
        className={`block text-${props.labelFontSize} font-${props.labelFontWeight} mb-1`}
        style={{ color: resolveColor(props.labelColor) }}
      >
        {props.label}
        {props.required && <span className="text-red-500 ml-0.5">*</span>}
      </label>

      {props.fieldType === 'textarea' ? (
        <textarea
          id={ids.input}
          rows={props.rows || 4}
          placeholder={props.placeholder || undefined}
          className={`${surface.classes} ${error ? 'border-red-400' : ''}`}
          style={surface.style}
          aria-invalid={error ? true : undefined}
          aria-describedby={error ? ids.error : props.helpText ? ids.help : undefined}
          {...register(props.label, rules)}
        />
      ) : (
        <input
          id={ids.input}
          type={inputTypeOf(props.fieldType)}
          placeholder={props.placeholder || undefined}
          className={`${surface.classes} ${error ? 'border-red-400' : ''}`}
          style={surface.style}
          aria-invalid={error ? true : undefined}
          aria-describedby={error ? ids.error : props.helpText ? ids.help : undefined}
          {...register(props.label, rules)}
        />
      )}

      {props.helpText && !error && (
        <p id={ids.help} className="mt-1 text-xs text-gray-500">{props.helpText}</p>
      )}
      {error && (
        <p role="alert" id={ids.error} className="mt-1 text-xs font-medium text-red-500">
          {String(error.message)}
        </p>
      )}
    </div>
  );
}

export const FormField: any = {
  ...Shared,
  render: (props: any) => <FormFieldLive {...props} />,
};

export default FormField;
